import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import {
  FaPlus, FaCheckCircle, FaUserAlt, FaLaptop,
  FaRegFileAlt, FaWhatsapp, FaTimes, FaLayerGroup,
  FaUsers, FaUserCheck, FaMoneyBillWave, FaBullseye,
  FaSearch, FaSyncAlt, FaAngleDown, FaChartPie, FaQrcode
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../utils/api';
import useRolePermissions, { hasPermission } from '../hooks/useRolePermissions';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';

/* --------------------------------- APIs ---------------------------------- */
const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';
// Lean SLIITID -> {name, role} lookup (~3.8MB/~3s as list_basic's full
// `SELECT *` -- this page only ever needs the name/role, never NIC/address/
// password/etc, so list_directory is a small, fast fraction of that).
const API_MEMBERS      = 'https://dev.sliitleo.org/Backend/member_api.php?action=list_directory';
// board_registrations is a wholly separate table (filled by the public
// installation.sliitleos.org/board form) -- NOT a filter over
// installation_registrations. Same underlying database as this portal.
const API_BOARD        = 'https://dev.sliitleo.org/Backend/board_reg.php';
// Bank slips live on the SEPARATE installation.sliitleo.org project, whose
// uploads/ folder blocks all direct access (Require all denied). This
// portal's own backend fetches them server-to-server via a shared secret
// instead (Backend/slip_proxy.php), so viewing one needs no second login.
const API_SLIP_PROXY   = 'https://dev.sliitleo.org/Backend/slip_proxy.php';

/* -------------------------------- Config --------------------------------- */
const POLL_MS_VISIBLE = 5000;
const POLL_MS_HIDDEN  = 15000;
const TARGET = 500; // top presentation bar target

/* -------------------------------- Helpers -------------------------------- */
function pickInstallations(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.rows && Array.isArray(payload.rows)) return payload.rows;
  if (payload?.all && Array.isArray(payload.all)) return payload.all;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.installations && Array.isArray(payload.installations)) return payload.installations;
  return [];
}
async function toJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); } catch { throw new Error('Invalid JSON from server'); }
}
async function postForm(url, params) {
  const token = getToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
    body: params.toString(),
  });
  const raw = await res.text();
  let json; try { json = JSON.parse(raw); } catch { json = null; }
  return { ok: res.ok, status: res.status, raw, json };
}
function toWaPhone(v = '') {
  const digits = String(v).replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('94')) return digits;
  return '94' + digits.replace(/^0/, '');
}
function extractIdNameRole(m = {}) {
  const id = String(m.SLIITID ?? m.SLIITLeoID ?? m.SLIIT_Leo_ID ?? m.sliitid ?? m.sliitID ?? '').trim();
  const name = String(m.FullName ?? m.full_name ?? m.FullName ?? m.fullName ?? m.Name ?? m.name ?? '').trim();
  const role = String(m.Role ?? m.role ?? m.userRole ?? m.UserRole ?? '').trim();
  return { id, name, role };
}
function isVerifiedMember(i){ return i.Verify === 1 || i.Verify === '1' || !!i.VerifiedDate; }
function getRegDate(i){ return i.RegDate || i.RegisteredDate || i.CreatedAt || i.CreatedDate || i.created_at || ''; }
function fmtDate(d){ try { return d ? new Date(d).toLocaleDateString() : ''; } catch { return ''; } }
function fmtDateTime(d){ try { return d ? new Date(d).toLocaleString() : ''; } catch { return ''; } }

/* ---------- board registrations (separate table: board_registrations) ---- */
function isBoardVerified(b){ return b.verified === 1 || b.verified === '1' || String(b.status || '').toUpperCase() === 'PAID'; }
function fmtBoardDate(d){ try { return d ? new Date(d).toLocaleDateString() : ''; } catch { return ''; } }

/* ---------- participation + cash ---------- */
function getParticipationFlag(i){
  // Uses ONLY the PartStatus column (as you requested). Mirrored server-side
  // by installation_participation_flag() in installation-qr/participation.php
  // (which blocks QR check-in) -- keep the two in sync.
  const v = String(i.PartStatus ?? '').trim().toLowerCase();
  if (!v) return null;
  if (['1','yes','true','y','attend','attending','participate','participating','will attend','confirmed'].includes(v)) return true;
  if (['0','no','false','n','not attend','not attending','not participating','not participate','decline','declined','cancelled'].includes(v)) return false;
  if (v.includes('not') && (v.includes('attend') || v.includes('particip'))) return false;
  if (v.includes('attend') || v.includes('particip')) return true;
  return null;
}
function isCashInHand(i){
  // `PaymenMethod` (sic) is the real DB/API field spelling -- checked first.
  // The extra-`t` `PaymentMethod` never matches any real row, which is why
  // KOKO/cash payments were silently falling through to blank before.
  const raw = String(
    i.PaymenMethod ?? i.PaymentMethod ?? i.PayMethod ?? i.PaymentType ?? i.Method ?? i.Bank ?? ''
  ).toLowerCase().trim();
  if (!raw) return false;
  return raw === 'cash' || raw === 'cash in hand' || raw === 'cash-in-hand' || raw === 'cash hand' || raw.includes('cash');
}
function isKokoPayment(i){
  return String(i.PaymenMethod ?? i.PaymentMethod ?? '').trim().toUpperCase() === 'KOKO';
}
function isCheckedIn(i){ return i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true; }
// Registered as not participating but turned up anyway -- the QR scanner
// refuses these, so every one of them was checked in manually.
function isNotPartAttended(i){ return getParticipationFlag(i) === false && isCheckedIn(i); }
function isEmailNotSent(i){
  // Mirrors installation_reg.php's own gate for the "Notify" action: the
  // confirmation/QR email is only ever attempted once a row is verified, so
  // an unverified row not having one yet isn't a gap to flag here.
  return isVerifiedMember(i) && !i.EmailSentAt;
}

// Best display label for a registrant's payment method: Cash-in-hand first,
// then the specific bank name for Bank Transfer rows (e.g. "Commercial
// Bank"), then whatever PaymenMethod says (e.g. "KOKO"), then Bank as a
// last resort.
function getPaymentMethodLabel(i) {
  if (isCashInHand(i)) return 'Cash-in-hand';
  const method = String(i.PaymenMethod ?? i.PaymentMethod ?? '').trim();
  if (/^bank transfer$/i.test(method) && i.Bank) return String(i.Bank).trim();
  if (method) return method;
  if (i.Bank) return String(i.Bank).trim();
  return '';
}

/* -------------------------- Filter chip definitions ------------------------ */
const SEGMENTS = [
  { key: 'all',          label: 'All',            countKey: 'total' },
  { key: 'verified',     label: 'Verified',       countKey: 'verified' },
  { key: 'unverified',   label: 'Unverified',     countKey: 'unverified' },
  { key: 'checked',      label: 'Checked-in',     countKey: 'checked' },
  { key: 'notchecked',   label: 'Not Checked-in', countKey: 'notchecked' },
  { key: 'cash',         label: 'Cash-in-hand',   countKey: 'cashCount' },
  { key: 'koko',         label: 'KOKO Payment',   countKey: 'kokoCount' },
  { key: 'emailnotsent', label: 'Email Not Sent', countKey: 'emailNotSentCount' },
  { key: 'notpart',      label: 'Not Participating', countKey: 'notParticipate' },
  { key: 'notpartattended', label: 'Not Participating · Attended', countKey: 'notPartAttendedCount' },
];

/* ================================ PAGE =================================== */
export default function Installation() {
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '',
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png'
  };

  const [verifyPopup, setVerifyPopup] = useState({ open: false, regid: null });
  const [reportModal, setReportModal] = useState({ open: false, regid: null, reason: '', loading: false, error: '' });
  // One-time Email/Participation correction (installation_reg.php action=edit)
  const EDIT_CLOSED = { open: false, item: null, email: '', part: '', loading: false, error: '' };
  const [editModal, setEditModal] = useState(EDIT_CLOSED);

  // Members (installation_registrations) vs Board Members
  // (board_registrations) — two entirely separate tables, two separate
  // tabs, never mixed.
  const [view, setView] = useState('members'); // 'members' | 'board'
  const [notifyingId, setNotifyingId] = useState(null);
  // Collapses the stats tiles + charts down to a one-line fill-percentage
  // summary. Purely a display toggle -- StatsOverview/AnalyticsPanel stay
  // mounted with the same props, so re-expanding shows the exact same view.
  const [detailsOpen, setDetailsOpen] = useState(true);
  // In-app echo of the push notification's own text, dismissible per tab --
  // sessionStorage (not localStorage) so it naturally reappears next visit
  // rather than needing its own persistence/clear-it-later mechanism.
  const [progressBannerDismissed, setProgressBannerDismissed] = useState(
    () => { try { return sessionStorage.getItem('installation_progress_banner_dismissed') === '1'; } catch { return false; } }
  );
  const dismissProgressBanner = () => {
    setProgressBannerDismissed(true);
    try { sessionStorage.setItem('installation_progress_banner_dismissed', '1'); } catch {}
  };

  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all'); // all | verified | unverified | checked | notchecked | cash
  const [sort, setSort] = useState('recent');   // recent | oldest | name

  const [rawInstallations, setRawInstallations] = useState([]);
  const [idToMeta, setIdToMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Board registrations state (independent of the member list above)
  const [rawBoardRegs, setRawBoardRegs] = useState([]);
  const [boardSearch, setBoardSearch] = useState('');
  const [boardSort, setBoardSort] = useState('recent');
  const [boardLoading, setBoardLoading] = useState(true);
  const [boardErr, setBoardErr] = useState('');
  const [boardNotifyingId, setBoardNotifyingId] = useState(null);
  const [boardVerifyingId, setBoardVerifyingId] = useState(null);
  const [boardVerifyPopup, setBoardVerifyPopup] = useState({ open: false, id: null });

  // UX: show “new items” toast when fresh records appear
  const [newCountToast, setNewCountToast] = useState(0);
  const lastSigRef = useRef('');
  const pollTimer = useRef(null);
  const currentPollAbort = useRef(null);

  /* ----------------------------- Boot & Poll ----------------------------- */
  const loadMemberDirectory = async () => {
    try {
      const res = await fetch(API_MEMBERS, { method: 'GET', cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const map = {};
      for (const m of arr) {
        const { id, name, role } = extractIdNameRole(m);
        if (id) map[id] = { name: name || '', role: role || '' };
      }
      setIdToMeta(map);
    } catch { setIdToMeta({}); }
  };

  const signature = (arr) => {
    try {
      const compact = arr.map(r => `${r.RegID ?? ''}|${r.SLIITID ?? ''}|${r.UpdatedAt ?? r.VerifiedDate ?? r.CheckedDate ?? r.RegDate ?? ''}|${r.CheckedDate ?? ''}|${r.EditedAt ?? ''}|${r.EmailSentAt ?? ''}`).sort().join('~');
      let hash = 0;
      for (let i=0;i<compact.length;i++) hash = (hash*31 + compact.charCodeAt(i))|0;
      return String(hash);
    } catch { return ''+Date.now(); }
  };

  const loadInstallations = async (signal) => {
    setErr('');
    try {
      // add a small cache-buster tied to last update time
      const url = API_INSTALLATION + (API_INSTALLATION.includes('?') ? '&' : '?') + 't=' + Date.now();
      const token = getToken();
      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const next = pickInstallations(data);
      const nextSig = signature(next);
      const prevSig = lastSigRef.current;

      // update state only if data changed (prevents flicker)
      if (nextSig !== prevSig) {
        const prevLen = rawInstallations.length;
        setRawInstallations(next);
        setLastUpdated(new Date());
        lastSigRef.current = nextSig;

        const delta = Math.max(0, next.length - prevLen);
        if (delta > 0) {
          setNewCountToast(delta);
          // auto-hide toast after a bit
          window.clearTimeout((loadInstallations._toastTO));
          loadInstallations._toastTO = window.setTimeout(()=>setNewCountToast(0), 3500);
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErr('Could not load registrations. Tap reload to try again.');
    }
  };

  const loadBoardRegs = async (signal) => {
    setBoardErr('');
    try {
      const url = API_BOARD + (API_BOARD.includes('?') ? '&' : '?') + 't=' + Date.now();
      const token = getToken();
      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const next = pickInstallations(data); // same {rows:[...]} / {data:[...]} shapes
      setRawBoardRegs(next);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setBoardErr('Could not load board registrations. Tap reload to try again.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setBoardLoading(true);
      await Promise.all([loadMemberDirectory(), loadInstallations(), loadBoardRegs()]);
      setLoading(false);
      setBoardLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function schedulePoll(ms) {
      clearInterval(pollTimer.current);
      pollTimer.current = setInterval(() => {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
        loadBoardRegs(ctrl.signal);
      }, ms);
    }
    schedulePoll(POLL_MS_VISIBLE);
    const onVis = () => {
      schedulePoll(document.visibilityState === 'visible' ? POLL_MS_VISIBLE : POLL_MS_HIDDEN);
      if (document.visibilityState === 'visible') {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
        loadBoardRegs(ctrl.signal);
      }
    };
    const onFocus = () => {
      if (currentPollAbort.current) currentPollAbort.current.abort();
      const ctrl = new AbortController();
      currentPollAbort.current = ctrl;
      loadInstallations(ctrl.signal);
      loadBoardRegs(ctrl.signal);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      clearInterval(pollTimer.current);
      if (currentPollAbort.current) currentPollAbort.current.abort();
    };
  }, []);

  /* --------------------------- Derivations ------------------------------- */
  const installations = useMemo(() => rawInstallations.map(r => ({
    ...r,
    VerifierName: r.VerifierName || r.VerifiedByName || (r.VerifiedBy ? (idToMeta[String(r.VerifiedBy)]?.name || '') : '') || '',
    CheckerName : r.CheckerName  || r.CheckedByName  || (r.CheckedBy  ? (idToMeta[String(r.CheckedBy )]?.name || '') : '') || ''
  })), [rawInstallations, idToMeta]);

  // The member table (installations) always shows Members-tab data. Board
  // registrations are a wholly separate table/state (rawBoardRegs) rendered
  // by its own section further down when view === 'board'.
  const viewInstallations = installations;

  const currentUserRole = useMemo(() => {
    const meta = idToMeta[String(user.SLIITID)] || {};
    return (meta.role || user.role || '').trim();
  }, [idToMeta, user.SLIITID, user.role]);

  // Verify/Check-in visibility is manageable per-role from Role & Path
  // Management (the 'installation_verify'/'installation_checkin'
  // permission keys) -- but only once an admin has explicitly saved
  // permission rows for a given role there. Until then, the fallback list
  // below reproduces today's exact behavior (System Admin only for both),
  // so nothing changes for anyone unless Path Management is used. See
  // src/hooks/useRolePermissions.js for why this can't share a fallback
  // check with route/nav access.
  const rolePerm = useRolePermissions(currentUserRole);
  const isSystemAdmin = hasPermission('installation_verify', rolePerm, currentUserRole, ['System Admin']);
  const isAnyAdmin = hasPermission('installation_checkin', rolePerm, currentUserRole, ['System Admin']);

  const totals = useMemo(() => {
    const total = viewInstallations.length;
    const checked = viewInstallations.filter(i => i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true).length;
    const verified = viewInstallations.filter(isVerifiedMember).length;
    const unverified = total - verified;
    const notchecked = total - checked;

    let participate = 0, notParticipate = 0;
    let cashCount = 0, kokoCount = 0, emailNotSentCount = 0, notPartAttendedCount = 0;
    const food = viewInstallations.reduce((acc, item) => {
      const raw = String(item.FoodPref || '').trim().toLowerCase();
      if (/^veg(?:etarian)?$/.test(raw) || raw === 'vegan') acc.veg++;
      else if (/^non[\s-]?veg(?:etarian)?$/.test(raw) || raw === 'non') acc.non++;
      else if (raw) acc.other++; else acc.unset++;
      return acc;
    }, { veg:0, non:0, other:0, unset:0 });

    for (const it of viewInstallations){
      const pf = getParticipationFlag(it);
      if (pf === true) participate++;
      else if (pf === false) notParticipate++;
      if (isCashInHand(it)) cashCount++;
      if (isKokoPayment(it)) kokoCount++;
      if (isEmailNotSent(it)) emailNotSentCount++;
      if (isNotPartAttended(it)) notPartAttendedCount++;
    }

    return {
      total, checked, verified, unverified, notchecked, food, participate, notParticipate,
      cashCount, kokoCount, emailNotSentCount, notPartAttendedCount,
    };
  }, [viewInstallations]);

  const pctToTarget = TARGET ? Math.min(100, Math.round((totals.total / TARGET) * 100)) : 0;

  // Member-only, matching exactly what check_new_registrations.php's push
  // notification reports ($memberTotal against REGISTRATION_TARGET=500,
  // board registrations deliberately excluded from this number on both
  // sides) -- the large progress bar/banner below reuses pctToTarget/
  // totals.total directly so the in-app number and the push never
  // silently disagree.
  const latestRegistrant = useMemo(() => {
    const candidates = installations
      .map(i => ({ name: i.Name || '', at: getRegDate(i) }))
      .filter(c => c.name && c.at);
    if (!candidates.length) return '';
    candidates.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return candidates[0].name;
  }, [installations]);

  const filtered = useMemo(() => {
    let list = [...viewInstallations];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        String(i.Name || '').toLowerCase().includes(q) ||
        String(i.SLIITID || '').toLowerCase().includes(q) ||
        String(i.KokoMerchantOrderId || '').toLowerCase().includes(q)
      );
    }
    list = list.filter(i => {
      if (segment === 'verified')   return isVerifiedMember(i);
      if (segment === 'unverified') return !isVerifiedMember(i);
      if (segment === 'checked')    return i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true;
      if (segment === 'notchecked') return !(i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true);
      if (segment === 'cash')       return isCashInHand(i);
      if (segment === 'koko')       return isKokoPayment(i);
      if (segment === 'emailnotsent') return isEmailNotSent(i);
      if (segment === 'notpart')    return getParticipationFlag(i) === false;
      if (segment === 'notpartattended') return isNotPartAttended(i);
      return true;
    });
    list.sort((a,b) => {
      if (sort === 'name')   return String(a.Name||'').localeCompare(String(b.Name||''));
      const da = new Date(getRegDate(a) || a.created_at || 0).getTime();
      const db = new Date(getRegDate(b) || b.created_at || 0).getTime();
      return sort === 'oldest' ? (da - db) : (db - da);
    });
    return list;
  }, [viewInstallations, search, segment, sort]);

  /* --------------------------- Board Derivations -------------------------- */
  const boardTotals = useMemo(() => {
    const total = rawBoardRegs.length;
    const verified = rawBoardRegs.filter(isBoardVerified).length;
    const unverified = total - verified;
    const koko = rawBoardRegs.filter(b => String(b.payment_method || '').toUpperCase() === 'KOKO').length;
    const bank = total - koko;
    const notSent = rawBoardRegs.filter(b => isBoardVerified(b) && !b.EmailSentAt).length;
    return { total, verified, unverified, koko, bank, notSent };
  }, [rawBoardRegs]);

  const boardFiltered = useMemo(() => {
    let list = [...rawBoardRegs];
    if (boardSearch.trim()) {
      const q = boardSearch.toLowerCase();
      list = list.filter(b =>
        String(b.name || '').toLowerCase().includes(q) ||
        String(b.sliit_id || '').toLowerCase().includes(q) ||
        String(b.board_position || '').toLowerCase().includes(q) ||
        String(b.KokoMerchantOrderId || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      if (boardSort === 'name') return String(a.name || '').localeCompare(String(b.name || ''));
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return boardSort === 'oldest' ? (da - db) : (db - da);
    });
    return list;
  }, [rawBoardRegs, boardSearch, boardSort]);

  /* ------------------------------ Actions -------------------------------- */
  const handleVerify = (regid) => { if (isSystemAdmin) setVerifyPopup({ open:true, regid }); };
  const handleVerifyCancel = () => setVerifyPopup({ open:false, regid:null });

  const handleVerifyAccept = async () => {
    if (!isSystemAdmin) return;
    const regid = verifyPopup.regid;
    setVerifyPopup({ open:false, regid:null });
    try {
      const params = new URLSearchParams();
      params.set('action','verify');
      params.set('RegID', String(regid));
      params.set('Verify','1');
      params.set('VerifiedBy', String(user.SLIITID || ''));
      params.set('VerifiedByName', String(user.name || 'System Admin'));
      params.set('SendEmail','1');
      params.set('EmailType','verified');
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        Verify: 1,
        VerifiedBy: user.SLIITID,
        VerifierName: json.VerifierName || r.VerifierName || user.name || '',
        VerifiedDate: json.VerifiedDate || r.VerifiedDate || new Date().toISOString(),
        EmailSentAt: json.email_sent ? (r.EmailSentAt || new Date().toISOString()) : r.EmailSentAt,
      } : r));
      if (json.email_sent === false && json.email_error) {
        alert('Verified, but the confirmation email could not be sent: ' + json.email_error + '\nUse "Send QR Email" to retry.');
      }
    } catch (e) {
      alert(e.message || 'Failed to verify');
    }
  };

  const handleCheckIn = async (regid, currentStatus, notParticipating) => {
    if (!isAnyAdmin) return;
    if (currentStatus === 1 || currentStatus === '1' || currentStatus === true) return;
    if (notParticipating && !window.confirm(
      'This member registered as NOT participating.\n\nCheck them in manually anyway? ' +
      'They will be listed under "Not Participating · Attended".'
    )) return;
    try {
      const params = new URLSearchParams();
      params.set('action','checkin');
      params.set('RegID', String(regid));
      params.set('CheckIn','1');
      params.set('CheckedBy', String(user.SLIITID || ''));
      params.set('CheckedByName', String(user.name || 'Admin'));
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        CheckIn: 1,
        CheckedBy: user.SLIITID,
        CheckerName: json.CheckerName || r.CheckerName || user.name || '',
        CheckedDate: json.CheckedDate || r.CheckedDate || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to check-in');
    }
  };

  const handleNotify = async (regid) => {
    if (!isSystemAdmin || notifyingId) return;
    setNotifyingId(regid);
    try {
      const params = new URLSearchParams();
      params.set('action','notify');
      params.set('RegID', String(regid));
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.email_error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        EmailSentAt: r.EmailSentAt || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to send confirmation email');
    } finally {
      setNotifyingId(null);
    }
  };

  const openEdit = (item) => {
    if (!isSystemAdmin || item.EditedAt) return;
    const pf = getParticipationFlag(item);
    setEditModal({
      ...EDIT_CLOSED,
      open: true,
      item,
      email: String(item.Email || ''),
      part: pf === true ? 'Yes' : (pf === false ? 'No' : ''),
    });
  };

  const handleEditSubmit = async () => {
    const { item, email, part } = editModal;
    if (!isSystemAdmin || !item) return;
    const newEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEditModal(prev => ({ ...prev, error: 'Enter a valid email address.' })); return;
    }
    if (part !== 'Yes' && part !== 'No') {
      setEditModal(prev => ({ ...prev, error: 'Choose a participation option.' })); return;
    }
    const pf = getParticipationFlag(item);
    const oldPart = pf === true ? 'Yes' : (pf === false ? 'No' : '');
    const emailChanged = newEmail.toLowerCase() !== String(item.Email || '').trim().toLowerCase();
    if (!emailChanged && part === oldPart) {
      setEditModal(prev => ({ ...prev, error: 'Nothing changed.' })); return;
    }

    setEditModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const params = new URLSearchParams();
      params.set('action', 'edit');
      params.set('RegID', String(item.RegID));
      params.set('Email', newEmail);
      params.set('PartStatus', part);
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) {
        const msg = json?.message === 'already_edited'
          ? 'This registration has already been edited once.'
          : (json?.message || json?.error || `HTTP ${status}`);
        throw new Error(msg);
      }
      setRawInstallations(list => list.map(r => r.RegID === item.RegID ? {
        ...r,
        Email: json.Email,
        PartStatus: json.PartStatus,
        OrigEmail: json.OrigEmail,
        OrigPartStatus: json.OrigPartStatus,
        EditedAt: json.EditedAt,
        EditedBy: user.SLIITID,
        EmailSentAt: json.email_changed
          ? (json.email_resent ? new Date().toISOString() : null)
          : r.EmailSentAt,
      } : r));
      setEditModal(EDIT_CLOSED);
      if (json.email_changed && json.email_error) {
        alert('Saved, but the QR email could not be re-sent: ' + json.email_error + '\nUse "Send QR Email" to retry.');
      } else if (json.email_resent) {
        alert('Saved. The QR email was re-sent to ' + json.Email + '.');
      }
    } catch (e) {
      setEditModal(prev => ({ ...prev, loading: false, error: e.message || 'Failed to save' }));
    }
  };

  /* --------------------------- Board Actions ------------------------------ */
  const handleBoardVerify = (id) => { if (isSystemAdmin) setBoardVerifyPopup({ open: true, id }); };
  const handleBoardVerifyCancel = () => setBoardVerifyPopup({ open: false, id: null });

  const handleBoardVerifyAccept = async () => {
    if (!isSystemAdmin) return;
    const id = boardVerifyPopup.id;
    setBoardVerifyPopup({ open: false, id: null });
    setBoardVerifyingId(id);
    try {
      const params = new URLSearchParams();
      params.set('action', 'verify');
      params.set('id', String(id));
      const { ok, json, status } = await postForm(API_BOARD, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawBoardRegs(list => list.map(r => r.id === id ? {
        ...r,
        verified: 1,
        status: 'PAID',
        EmailSentAt: json.email_sent ? (r.EmailSentAt || new Date().toISOString()) : r.EmailSentAt,
      } : r));
      if (json.email_sent === false && json.email_error) {
        alert('Verified, but the confirmation email could not be sent: ' + json.email_error + '\nUse "Send QR Email" to retry.');
      }
    } catch (e) {
      alert(e.message || 'Failed to verify');
    } finally {
      setBoardVerifyingId(null);
    }
  };

  const handleBoardNotify = async (id) => {
    if (!isSystemAdmin || boardNotifyingId) return;
    setBoardNotifyingId(id);
    try {
      const params = new URLSearchParams();
      params.set('action', 'notify');
      params.set('id', String(id));
      const { ok, json, status } = await postForm(API_BOARD, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.email_error || `HTTP ${status}`);
      setRawBoardRegs(list => list.map(r => r.id === id ? {
        ...r,
        EmailSentAt: r.EmailSentAt || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to send confirmation email');
    } finally {
      setBoardNotifyingId(null);
    }
  };

  const handleReportSubmit = async () => {
    if (!isAnyAdmin) { setReportModal(prev => ({ ...prev, error: 'Only admins can report registrations.' })); return; }
    if (!reportModal.reason.trim()) { setReportModal(prev => ({ ...prev, error: 'Please enter a reason' })); return; }
    try {
      setReportModal(prev => ({ ...prev, loading:true, error:'' }));
      const formData = new FormData();
      formData.append('action','report');
      formData.append('RegID', reportModal.regid);
      formData.append('ReportReason', reportModal.reason);
      formData.append('ReportedBy', user.SLIITID || 'admin');
      formData.append('ReportedDate', new Date().toISOString().slice(0,19).replace('T',' '));
      const token = getToken();
      const response = await axios.post(API_INSTALLATION, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.data?.success) throw new Error(response.data?.error || 'Failed to submit');
      setRawInstallations(list => list.map(r => r.RegID === reportModal.regid ? { ...r, ReportReason: reportModal.reason } : r));
      setReportModal({ open:false, regid:null, reason:'', loading:false, error:'' });
    } catch (e) {
      setReportModal(prev => ({ ...prev, error: e.message || 'Failed', loading:false }));
    }
  };

  /* ------------------------------- UI ------------------------------------ */
  if (loading) {
    return (
      <>
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 px-4 sm:px-6 py-6 sm:py-8">
          <div className="page-head">
            <div>
              <h1><FaLaptop style={{ marginRight: 10, color: 'var(--accent)' }} />Installation</h1>
              <p className="page-desc">Annual installation ceremony — registration &amp; check-in</p>
            </div>
          </div>

          <div className="priority">
            <div className="panel hero-metric">
              <div className="skeleton" style={{ height: 16, width: 130 }} />
              <div className="skeleton" style={{ height: 36, width: 90 }} />
              <div className="skeleton" style={{ height: 12, width: 160 }} />
            </div>
            <div className="panel tile">
              <div className="skeleton" style={{ height: 14, width: 80 }} />
              <div className="skeleton" style={{ height: 26, width: 50 }} />
            </div>
            <div className="panel tile">
              <div className="skeleton" style={{ height: 14, width: 80 }} />
              <div className="skeleton" style={{ height: 26, width: 50 }} />
            </div>
          </div>

          <div className="panel" style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-sm)' }} />
            ))}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      {/* Verify modal (System Admin only) */}
      {verifyPopup.open && isSystemAdmin && (
        <div className="modal-overlay">
          <div className="modal-panel panel">
            <div className="modal-head">
              <h3>Verify Registration</h3>
              <button onClick={handleVerifyCancel} className="icon-btn" aria-label="Close">
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px',
                  display: 'grid', placeItems: 'center',
                  background: 'rgba(63,174,106,.14)', color: 'var(--success)',
                }}
              >
                <FaCheckCircle size={22} />
              </div>
              <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>
                Confirm you want to <strong style={{ color: 'var(--text-1)' }}>verify</strong> this registration.
              </p>
            </div>
            <div className="modal-foot">
              <button onClick={handleVerifyCancel} className="btn btn-ghost">Cancel</button>
              <button onClick={handleVerifyAccept} className="btn btn-primary">Accept</button>
            </div>
          </div>
        </div>
      )}

      {/* One-time Email / Participation edit (System Admin only) */}
      {editModal.open && isSystemAdmin && editModal.item && (
        <div className="modal-overlay">
          <div className="modal-panel panel lg">
            <div className="modal-head">
              <h3>Edit Registration</h3>
              <button onClick={() => setEditModal(EDIT_CLOSED)} className="icon-btn" aria-label="Close" disabled={editModal.loading}>
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 12 }}>
                <strong style={{ color: 'var(--text-1)' }}>{editModal.item.Name}</strong>
                {' · '}<span className="num">{editModal.item.SLIITID}</span>
              </p>
              <div className="field">
                <label className="field-label" htmlFor="inst-edit-email">Email <span className="req">*</span></label>
                <input
                  id="inst-edit-email"
                  type="email"
                  value={editModal.email}
                  onChange={(e) => setEditModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="inst-edit-part">Participating in the ceremony? <span className="req">*</span></label>
                <select
                  id="inst-edit-part"
                  value={editModal.part}
                  onChange={(e) => setEditModal(prev => ({ ...prev, part: e.target.value, error: '' }))}
                >
                  <option value="" disabled>Select…</option>
                  <option value="Yes">Yes — Participating</option>
                  <option value="No">No — Not Participating</option>
                </select>
              </div>
              <div className="error-banner" style={{ fontSize: 12.5 }}>
                This can be done only ONCE per registration. Changing the email
                {isVerifiedMember(editModal.item) ? ' re-sends the QR email to the new address' : ' means the QR email goes to the new address when verified'};
                changing only participation sends no email.
              </div>
              {editModal.error && <div className="field-error" style={{ marginTop: 8 }}>{editModal.error}</div>}
            </div>
            <div className="modal-foot">
              <button type="button" onClick={() => setEditModal(EDIT_CLOSED)} className="btn btn-ghost" disabled={editModal.loading}>
                Cancel
              </button>
              <button type="button" onClick={handleEditSubmit} className="btn btn-primary" disabled={editModal.loading}>
                {editModal.loading && <span className="spinner" style={{ marginRight: 6 }} />}
                {editModal.loading ? 'Saving…' : 'Save (one time)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report modal (admins) */}
      {reportModal.open && isAnyAdmin && (
        <div className="modal-overlay">
          <div className="modal-panel panel lg">
            <div className="modal-head">
              <h3>Report Issue</h3>
              <button
                onClick={() => setReportModal(prev => ({ ...prev, open: false }))}
                className="icon-btn"
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label" htmlFor="inst-report-reason">
                  Reason <span className="req">*</span>
                </label>
                <textarea
                  id="inst-report-reason"
                  rows={4}
                  value={reportModal.reason}
                  onChange={(e) => setReportModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Add details..."
                  className={reportModal.error ? 'invalid' : ''}
                />
                {reportModal.error && <div className="field-error">{reportModal.error}</div>}
              </div>
            </div>
            <div className="modal-foot">
              <button
                type="button"
                onClick={() => setReportModal(prev => ({ ...prev, open: false }))}
                className="btn btn-ghost"
                disabled={reportModal.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReportSubmit}
                className="btn btn-danger"
                disabled={reportModal.loading}
              >
                {reportModal.loading && <span className="spinner" style={{ marginRight: 6 }} />}
                {reportModal.loading ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="lg:ml-64 mt-14 px-4 sm:px-6 py-6 sm:py-8">
        {/* tiny toast for new data */}
        {newCountToast > 0 && (
          <div
            className="success-banner"
            style={{ width: 'fit-content', margin: '0 auto var(--sp-4)', position: 'sticky', top: 8, zIndex: 30 }}
          >
            <FaCheckCircle />
            <span>+{newCountToast} new record{newCountToast > 1 ? 's' : ''} loaded</span>
          </div>
        )}

        <div className="page-head">
          <div>
            <h1><FaLaptop style={{ marginRight: 10, color: 'var(--accent)' }} />Installation</h1>
            <p className="page-desc">Annual installation ceremony — registration &amp; check-in</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/installation/scan" className="btn btn-primary" title="Scan a registrant's QR code to check them in">
              <FaQrcode /> Scan Check-in
            </Link>
            <Link to="/add-registration" className="btn btn-ghost" title="Add a new registration">
              <FaPlus /> Add Registration
            </Link>
            <Link to="/installation-report" className="btn btn-ghost" title="Open summary dashboard">
              <FaLayerGroup /> Summary
            </Link>
          </div>
        </div>

        {/* Members vs Board Members — separate tabs, separate tables (separate
            DB tables too: installation_registrations vs board_registrations) */}
        <div className="filter-row" style={{ marginBottom: 'var(--sp-4)' }}>
          <button
            type="button"
            className={`chip${view === 'members' ? ' active' : ''}`}
            onClick={() => setView('members')}
          >
            Members <span className="num">{installations.length}</span>
          </button>
          <button
            type="button"
            className={`chip${view === 'board' ? ' active' : ''}`}
            onClick={() => setView('board')}
          >
            Board Members <span className="num">{rawBoardRegs.length}</span>
          </button>
        </div>

        {view === 'members' && (
          <>
            {/* Large registration-progress bar -- members only, matching
                exactly what the "Installation Registrations" push
                notification reports (board registrations tracked
                separately, never folded into this number). Always visible
                above the collapse toggle, in both collapsed and expanded
                states. The pulsing "Live" dot reflects the same 5s/15s
                background poll this whole page already runs (see
                POLL_MS_VISIBLE/POLL_MS_HIDDEN above) -- this bar updates on
                its own, no manual refresh needed. */}
            <div className="panel" style={{ marginBottom: 'var(--sp-4)', padding: 'var(--sp-4)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaUsers style={{ color: 'var(--accent)' }} aria-hidden="true" />
                  <strong style={{ fontSize: 13.5 }}>Registration Progress</strong>
                  <span className="live-dot" title="Updates automatically -- no need to refresh">
                    <span className="live-dot-pulse" aria-hidden="true" />
                    LIVE
                  </span>
                </div>
                <div>
                  <span className="progress-track-lg-value num" style={{ fontSize: 22, color: 'var(--text-1)' }}>{pctToTarget}%</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 6 }}>
                    {totals.total} of {TARGET}
                  </span>
                </div>
              </div>
              <div
                className="progress-track-lg"
                style={{ '--pct': pctToTarget }}
                role="progressbar"
                aria-valuenow={pctToTarget}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Registration progress: ${pctToTarget}% of ${TARGET} target`}
              >
                <div className="fill" />
              </div>

              {!progressBannerDismissed && (
                <div
                  className="success-banner"
                  style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                >
                  <span>
                    {totals.total}/{TARGET} registered ({pctToTarget}%)
                    {latestRegistrant ? ` · Latest: ${latestRegistrant}` : ''}
                  </span>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={dismissProgressBanner}
                    aria-label="Dismiss"
                    style={{ flexShrink: 0 }}
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
            </div>

            <div
              className="panel"
              style={{
                marginBottom: detailsOpen ? 'var(--sp-4)' : 'var(--sp-6)',
                padding: 'var(--sp-3) var(--sp-4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
                <FaChartPie style={{ color: 'var(--accent)' }} aria-hidden="true" />
                <strong style={{ fontSize: 13.5 }}>Registration Overview</strong>
                {!detailsOpen && (
                  <span className="pill pill-info" style={{ marginLeft: 4 }}>
                    <span className="dot" />
                    {pctToTarget}% of {TARGET} target · {totals.total} registered
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setDetailsOpen(o => !o)}
                aria-expanded={detailsOpen}
                aria-label={detailsOpen ? 'Collapse stats and charts to a summary' : 'Expand full stats and charts'}
              >
                {detailsOpen ? 'Collapse to summary' : 'Show full details'}
                <FaAngleDown
                  style={{
                    marginLeft: 6,
                    transition: 'transform .2s ease',
                    transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                  aria-hidden="true"
                />
              </button>
            </div>

            {!detailsOpen && (
              <div className="panel" style={{ marginBottom: 'var(--sp-6)', padding: 'var(--sp-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div className="ring" style={{ '--pct': pctToTarget, flexShrink: 0 }} data-pct={`${pctToTarget}%`} aria-hidden="true" />
                  <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <div className="hero-value num" style={{ fontSize: 22 }}>{totals.total}</div>
                    <div className="hero-sub">
                      <FaBullseye style={{ marginRight: 6 }} aria-hidden="true" />
                      {pctToTarget}% of {TARGET} target
                      {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString()}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-2)' }}>
                    <span><FaUserCheck style={{ marginRight: 5 }} aria-hidden="true" />{totals.verified} verified</span>
                    <span><FaCheckCircle style={{ marginRight: 5 }} aria-hidden="true" />{totals.checked} checked-in</span>
                  </div>
                </div>
              </div>
            )}

            {detailsOpen && (
              <>
                <StatsOverview totals={totals} target={TARGET} pct={pctToTarget} lastUpdated={lastUpdated} />

                <ChartErrorBoundary>
                  <AnalyticsPanel totals={totals} />
                </ChartErrorBoundary>
              </>
            )}

            <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
              <div className="section-head" style={{ flexWrap: 'wrap', gap: 10 }}>
                <h2>Member Registrants</h2>
                <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                  {SEGMENTS.map(s => (
                    <button
                      key={s.key}
                      type="button"
                      className={`chip${segment === s.key ? ' active' : ''}`}
                      onClick={() => setSegment(s.key)}
                    >
                      {s.label} <span className="num">{totals[s.countKey]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '0 var(--sp-4) var(--sp-4)' }}>
                <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
                  <FaSearch
                    aria-hidden="true"
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }}
                  />
                  <input
                    type="text"
                    placeholder="Search by name, SLIIT ID or KOKO order ID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search registrations"
                    style={{ paddingLeft: 32 }}
                  />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" style={{ width: 'auto' }}>
                  <option value="recent">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">Name A→Z</option>
                </select>
                <button
                  type="button"
                  onClick={async () => { setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
                  className="btn btn-ghost btn-sm"
                >
                  <FaSyncAlt size={12} /> Reload
                </button>
              </div>

              {filtered.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><FaUsers /></div>
                  <h3>No registrations found</h3>
                  <p>{err || 'Try a different search or filter.'}</p>
                  {err && (
                    <button
                      onClick={async () => { setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
                      className="btn btn-primary btn-sm"
                    >
                      <FaSyncAlt size={11} /> Reload
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Registrant</th>
                        <th>Registered</th>
                        <th>Payment</th>
                        <th>Participation</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(item => (
                        <Row
                          key={item.RegID}
                          item={item}
                          isSystemAdmin={isSystemAdmin}
                          isAnyAdmin={isAnyAdmin}
                          isNotifying={notifyingId === item.RegID}
                          onVerify={() => handleVerify(item.RegID)}
                          onReport={() => setReportModal({ open: true, regid: item.RegID, reason: '', loading: false, error: '' })}
                          onCheckIn={() => handleCheckIn(item.RegID, item.CheckIn, getParticipationFlag(item) === false)}
                          onNotify={() => handleNotify(item.RegID)}
                          onEdit={() => openEdit(item)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {view === 'board' && (
          <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
            <div className="section-head" style={{ flexWrap: 'wrap', gap: 10 }}>
              <h2>Board Member Registrants</h2>
              <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                <span className="chip active">Total <span className="num">{boardTotals.total}</span></span>
                <span className="chip">Verified <span className="num">{boardTotals.verified}</span></span>
                <span className="chip">Unverified <span className="num">{boardTotals.unverified}</span></span>
                <span className="chip">KOKO <span className="num">{boardTotals.koko}</span></span>
                <span className="chip">Bank Transfer <span className="num">{boardTotals.bank}</span></span>
                <span className="chip">QR Not Sent <span className="num">{boardTotals.notSent}</span></span>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '0 var(--sp-4) var(--sp-4)' }}>
              <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
                <FaSearch
                  aria-hidden="true"
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }}
                />
                <input
                  type="text"
                  placeholder="Search by name, SLIIT ID, position or KOKO order ID…"
                  value={boardSearch}
                  onChange={(e) => setBoardSearch(e.target.value)}
                  aria-label="Search board registrations"
                  style={{ paddingLeft: 32 }}
                />
              </div>
              <select value={boardSort} onChange={(e) => setBoardSort(e.target.value)} aria-label="Sort" style={{ width: 'auto' }}>
                <option value="recent">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name A→Z</option>
              </select>
              <button
                type="button"
                onClick={async () => { setBoardLoading(true); await loadBoardRegs(); setBoardLoading(false); }}
                className="btn btn-ghost btn-sm"
              >
                <FaSyncAlt size={12} /> Reload
              </button>
            </div>

            {boardVerifyPopup.open && isSystemAdmin && (
              <div className="modal-overlay">
                <div className="modal-panel panel">
                  <div className="modal-head">
                    <h3>Confirm Bank Payment</h3>
                    <button onClick={handleBoardVerifyCancel} className="icon-btn" aria-label="Close">
                      <FaTimes size={14} />
                    </button>
                  </div>
                  <div className="modal-body" style={{ textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>
                      Confirm you want to <strong style={{ color: 'var(--text-1)' }}>verify</strong> this board registration's bank payment.
                    </p>
                  </div>
                  <div className="modal-foot">
                    <button onClick={handleBoardVerifyCancel} className="btn btn-ghost">Cancel</button>
                    <button onClick={handleBoardVerifyAccept} className="btn btn-primary">Accept</button>
                  </div>
                </div>
              </div>
            )}

            {boardLoading ? (
              <div className="panel" style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="skeleton" style={{ height: 52, borderRadius: 'var(--radius-sm)' }} />
                ))}
              </div>
            ) : boardFiltered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaUsers /></div>
                <h3>No board registrations found</h3>
                <p>{boardErr || 'Try a different search, or check installation.sliitleos.org/board has submissions.'}</p>
                {boardErr && (
                  <button
                    onClick={async () => { setBoardLoading(true); await loadBoardRegs(); setBoardLoading(false); }}
                    className="btn btn-primary btn-sm"
                  >
                    <FaSyncAlt size={11} /> Reload
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Registrant</th>
                      <th>Board Position</th>
                      <th>Registered</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boardFiltered.map(item => (
                      <BoardRow
                        key={item.id}
                        item={item}
                        isSystemAdmin={isSystemAdmin}
                        isVerifying={boardVerifyingId === item.id}
                        isNotifying={boardNotifyingId === item.id}
                        onVerify={() => handleBoardVerify(item.id)}
                        onNotify={() => handleBoardNotify(item.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </>
  );
}

/* ============================ Small Components =========================== */

function StatsOverview({ totals, target, pct, lastUpdated }) {
  const { food } = totals;
  return (
    <>
      <div className="priority">
        <div className="panel hero-metric">
          <div className="hero-top">
            <span className="eyebrow">Total Registered</span>
            <div className="ring" style={{ '--pct': pct }} data-pct={`${pct}%`} />
          </div>
          <div className="hero-value num">{totals.total}</div>
          <div className="hero-sub">
            <FaBullseye style={{ marginRight: 6 }} />
            {pct}% of {target} target
            {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString()}` : ''}
          </div>
          <div className="hero-list">
            <div className="hero-list-row"><span className="name">Veg</span><span className="when num">{food.veg}</span></div>
            <div className="hero-list-row"><span className="name">Non-Veg</span><span className="when num">{food.non}</span></div>
            <div className="hero-list-row"><span className="name">Other</span><span className="when num">{food.other}</span></div>
            <div className="hero-list-row"><span className="name">Not set</span><span className="when num">{food.unset}</span></div>
          </div>
        </div>

        <div className="panel tile">
          <div>
            <div className="tile-label"><FaUserCheck style={{ marginRight: 6 }} />Verified</div>
            <div className="tile-value num">{totals.verified}</div>
          </div>
          <div className={`tile-trend ${totals.unverified > 0 ? 'trend-warn' : 'trend-up'}`}>
            {totals.unverified} unverified
          </div>
        </div>

        <div className="panel tile">
          <div>
            <div className="tile-label"><FaCheckCircle style={{ marginRight: 6 }} />Checked-in</div>
            <div className="tile-value num">{totals.checked}</div>
          </div>
          <div className={`tile-trend ${totals.notchecked > 0 ? 'trend-flat' : 'trend-up'}`}>
            {totals.notchecked} remaining
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <div className="panel tile">
          <div className="tile-label"><FaMoneyBillWave style={{ marginRight: 6 }} />Cash-in-hand</div>
          <div className="tile-value num">{totals.cashCount}</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Participating</div>
          <div className="tile-value num">{totals.participate}</div>
          <div className="hero-sub">{totals.notParticipate} not participating</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Unverified</div>
          <div className="tile-value num">{totals.unverified}</div>
        </div>
        <div className="panel tile">
          <div className="tile-label">Not Checked-in</div>
          <div className="tile-value num">{totals.notchecked}</div>
        </div>
      </div>
    </>
  );
}

/* ----------------------------- Analytics charts --------------------------- */
function AnalyticsDonut({ title, data, colors }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{ width: 72, height: 72, flexShrink: 0 }}
          role="img"
          aria-label={`Donut chart of ${title.toLowerCase()}: ${data.map(d => `${d.name} ${d.value}`).join(', ')}.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={20}
                outerRadius={34}
                paddingAngle={total ? 2 : 0}
                stroke="none"
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#1c1c1c', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: '#ffffff' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          {data.map((d, i) => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: colors[i % colors.length], flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap' }}>{d.name}</span>
              <span className="num" style={{ marginLeft: 'auto', color: 'var(--text-1)', fontWeight: 700 }}>{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error('Installation analytics charts failed to render:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="panel" style={{ marginBottom: 'var(--sp-6)', textAlign: 'center', color: 'var(--text-3)' }}>
          Analytics unavailable in this browser.
        </div>
      );
    }
    return this.props.children;
  }
}

function AnalyticsPanel({ totals }) {
  const { food } = totals;
  const verifiedData = [
    { name: 'Verified', value: totals.verified },
    { name: 'Unverified', value: totals.unverified },
  ];
  const checkinData = [
    { name: 'Checked-in', value: totals.checked },
    { name: 'Not checked-in', value: totals.notchecked },
  ];
  const paymentData = [
    { name: 'Cash-in-hand', value: totals.cashCount },
    { name: 'Other/Bank', value: Math.max(0, totals.total - totals.cashCount) },
  ];
  const foodData = [
    { label: 'Veg', count: food.veg },
    { label: 'Non-Veg', count: food.non },
    { label: 'Other', count: food.other },
    { label: 'Not set', count: food.unset },
  ];

  return (
    <div className="panel" style={{ marginBottom: 'var(--sp-6)' }}>
      <div className="section-head">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaChartPie size={13} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Registration Analytics
        </h2>
      </div>
      <div
        style={{
          padding: '0 var(--sp-3) var(--sp-4)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--sp-3)',
        }}
      >
        <AnalyticsDonut title="Verification status" data={verifiedData} colors={['#3fae6a', '#e2645c']} />
        <AnalyticsDonut title="Check-in status" data={checkinData} colors={['#e11d2e', '#6c7ea3']} />
        <AnalyticsDonut title="Payment method" data={paymentData} colors={['#e11d2e', '#7a0d18']} />

        <div
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3)',
            minWidth: 0,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 6 }}>Food preference</div>
          <div
            style={{ width: '100%', height: 76 }}
            role="img"
            aria-label={`Bar chart of food preference: Veg ${food.veg}, Non-Veg ${food.non}, Other ${food.other}, Not set ${food.unset}.`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={foodData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.10)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#c9c9c9', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.16)' }} tickLine={false} />
                <YAxis tick={{ fill: '#c9c9c9', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#1c1c1c', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontSize: 11 }}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="count" fill="#e11d2e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Memoized row for speed */
const Row = memo(function Row({ item, isSystemAdmin, isAnyAdmin, isNotifying, onVerify, onReport, onCheckIn, onNotify, onEdit }) {
  const [open, setOpen] = useState(false);
  const wa = toWaPhone(item.Contact);
  const checked = item.CheckIn === 1 || item.CheckIn === '1' || item.CheckIn === true;
  const verified = isVerifiedMember(item);
  const isReported = !!(item.ReportReason && !/^(null|null|undefined|\s*)$/i.test(String(item.ReportReason)));
  const regDate = fmtDate(getRegDate(item));

  const partFlag = getParticipationFlag(item);
  const partBadge = partFlag === true ? 'Participating' : (partFlag === false ? 'Not Participating' : 'Unknown');
  const isCash = isCashInHand(item);
  const paymentLabel = getPaymentMethodLabel(item);

  const foodLabel = (() => {
    const fp = String(item.FoodPref || '').toLowerCase().trim();
    if (fp === 'veg' || fp === 'vegetarian') return 'Veg';
    if (fp === 'non-veg' || fp === 'nonveg' || fp === 'non vegetarian') return 'Non';
    return fp || 'N/A';
  })();

  return (
    <>
      <tr>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: '999px', flexShrink: 0,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                display: 'grid', placeItems: 'center', color: 'var(--text-2)',
              }}
            >
              <FaUserAlt size={13} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="proj-name truncate" title={item.Name}>{item.Name}</div>
              <div className="proj-cat truncate">
                <span className="num">{item.SLIITID}</span>{item.Faculty ? ` · ${item.Faculty}` : ''}
              </div>
            </div>
          </div>
        </td>

        <td>
          <div className="num">{regDate || '—'}</div>
          <div className="proj-cat">{foodLabel}</div>
        </td>

        <td>
          <div>{item.Amount || '—'}</div>
          {paymentLabel && (
            <span className={`pill ${isCash ? 'pill-warning' : 'pill-info'}`} style={{ marginTop: 4 }}>
              <span className="dot" />{paymentLabel}
            </span>
          )}
        </td>

        <td>
          {partFlag === true && <span className="pill pill-success"><span className="dot" />{partBadge}</span>}
          {partFlag === false && <span className="pill pill-danger"><span className="dot" />{partBadge}</span>}
          {partFlag === null && <span className="pill pill-info"><span className="dot" />{partBadge}</span>}
          {partFlag === false && checked && (
            <div className="proj-cat" style={{ marginTop: 4 }}>Attended (manual check-in)</div>
          )}
        </td>

        <td>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {verified
              ? <span className="pill pill-success"><span className="dot" />Verified</span>
              : <span className="pill pill-danger"><span className="dot" />Unverified</span>}
            {checked && <span className="pill pill-success"><span className="dot" />Checked-in</span>}
            {isReported && <span className="pill pill-danger"><span className="dot" />Reported</span>}
            {item.EditedAt && (
              <span className="pill pill-warning" title={`Edited ${fmtDateTime(item.EditedAt)}`}><span className="dot" />Edited</span>
            )}
          </div>
        </td>

        <td>
          <div className="row-actions" style={{ flexWrap: 'wrap' }}>
            {item.FileURL && (
              <button
                className="icon-btn"
                onClick={() => window.open(`${API_SLIP_PROXY}?id=${item.RegID}&scope=member`, '_blank')}
                title="View payment slip"
                aria-label="View payment slip"
                type="button"
              >
                <FaRegFileAlt size={13} />
              </button>
            )}
            {wa && (
              <a
                href={`https://wa.me/+${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-btn"
                title="Chat on WhatsApp"
                aria-label={`Chat on WhatsApp with ${item.Name || 'registrant'}`}
              >
                <FaWhatsapp size={13} />
              </a>
            )}
            {isSystemAdmin && !verified && !isReported && (
              <button className="btn btn-primary btn-sm" onClick={onVerify}>Verify</button>
            )}
            {isAnyAdmin && verified && !checked && (
              <button className="btn btn-primary btn-sm" onClick={onCheckIn}>Check-in</button>
            )}
            {isSystemAdmin && verified && !item.EmailSentAt && (
              <button className="btn btn-primary btn-sm" onClick={onNotify} disabled={isNotifying}>
                {isNotifying && <span className="spinner" style={{ marginRight: 6 }} />}
                {isNotifying ? 'Sending…' : 'Send QR Email'}
              </button>
            )}
            {item.EmailSentAt && (
              <span className="pill pill-success" title={fmtDateTime(item.EmailSentAt)}>
                <span className="dot" />Sent {fmtDate(item.EmailSentAt)}
              </span>
            )}
            {isSystemAdmin && !item.EditedAt && (
              <button className="btn btn-ghost btn-sm" onClick={onEdit} title="Correct email / participation (one time only)">Edit</button>
            )}
            {isAnyAdmin && !isReported && (
              <button className="btn btn-danger btn-sm" onClick={onReport}>Report</button>
            )}
            <button
              className="icon-btn"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-label={open ? 'Hide details' : 'Show details'}
              title={open ? 'Hide details' : 'Show details'}
            >
              <FaAngleDown style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }} size={14} />
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-2) 0',
              }}
            >
              <Detail label="PartStatus" value={item.PartStatus ?? '-'} />
              <Detail label="Food Pref" value={item.FoodPref ?? '-'} />
              <Detail label="Amount" value={item.Amount ?? '-'} />
              <Detail label="Payment" value={paymentLabel || '-'} />
              {String(item.PaymenMethod || '').toUpperCase() === 'KOKO' && (
                <>
                  <Detail label="KOKO Status" value={item.KokoPaymentStatus || '-'} />
                  <Detail label="KOKO Merchant Order ID" value={item.KokoMerchantOrderId || '-'} />
                  <Detail label="KOKO Transaction ID" value={item.KokoTransactionId || '-'} />
                  <Detail label="KOKO Reference" value={item.KokoReference || '-'} />
                  <Detail label="KOKO Provider Status" value={item.KokoProviderStatus || '-'} />
                  <Detail label="Signature Verified" value={item.KokoSignatureVerified === 1 || item.KokoSignatureVerified === '1' ? 'Yes' : 'No'} />
                  <Detail label="KOKO Paid At" value={fmtDateTime(item.KokoPaidAt) || '-'} />
                </>
              )}
              <Detail label="Contact" value={item.Contact ?? '-'} />
              <Detail label="Email" value={item.Email ?? '-'} />
              <Detail label="Verified By" value={item.VerifierName || '-'} />
              <Detail label="Verified Date" value={fmtDate(item.VerifiedDate)} />
              <Detail label="Checked By" value={item.CheckerName || '-'} />
              <Detail label="Checked Date" value={fmtDate(item.CheckedDate)} />
              <Detail label="Confirmation Email" value={item.EmailSentAt ? `Sent ${fmtDateTime(item.EmailSentAt)}` : 'Not sent'} />
              {item.EditedAt && (
                <>
                  <Detail label="Edited" value={fmtDateTime(item.EditedAt)} />
                  <Detail label="Email before edit" value={item.OrigEmail || '-'} />
                  <Detail label="PartStatus before edit" value={item.OrigPartStatus || '-'} />
                </>
              )}
            </div>
            {isReported && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--error)' }}>
                <strong style={{ color: 'var(--text-1)' }}>Reported reason:</strong> {item.ReportReason}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
});

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-1)', fontWeight: 600 }}>{String(value)}</div>
    </div>
  );
}

/* Board registration row — board_registrations has its own shape
   (id, sliit_id, board_position, payment_method, verified, status, ...),
   distinct from installation_registrations, so it gets its own row/table
   rather than being shoehorned into <Row>. */
const BoardRow = memo(function BoardRow({ item, isSystemAdmin, isVerifying, isNotifying, onVerify, onNotify }) {
  const [open, setOpen] = useState(false);
  const wa = toWaPhone(item.contact);
  const verified = isBoardVerified(item);
  const isKoko = String(item.payment_method || '').toUpperCase() === 'KOKO';
  const regDate = fmtBoardDate(item.created_at);

  return (
    <>
      <tr>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: '999px', flexShrink: 0,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                display: 'grid', placeItems: 'center', color: 'var(--text-2)',
              }}
            >
              <FaUserAlt size={13} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="proj-name truncate" title={item.name}>{item.name}</div>
              <div className="proj-cat truncate"><span className="num">{item.sliit_id}</span></div>
            </div>
          </div>
        </td>

        <td>{item.board_position || '—'}</td>

        <td>
          <div className="num">{regDate || '—'}</div>
          <div className="proj-cat">{item.participation || ''}</div>
        </td>

        <td>
          <div>{item.amount || '—'}</div>
          <span className={`pill ${isKoko ? 'pill-info' : 'pill-warning'}`} style={{ marginTop: 4 }}>
            <span className="dot" />{item.payment_method || 'N/A'}
          </span>
        </td>

        <td>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {verified
              ? <span className="pill pill-success"><span className="dot" />Verified</span>
              : <span className="pill pill-danger"><span className="dot" />Unverified</span>}
          </div>
        </td>

        <td>
          <div className="row-actions" style={{ flexWrap: 'wrap' }}>
            {item.file_url && (
              <button
                className="icon-btn"
                onClick={() => window.open(`${API_SLIP_PROXY}?id=${item.id}&scope=board`, '_blank')}
                title="View payment slip"
                aria-label="View payment slip"
                type="button"
              >
                <FaRegFileAlt size={13} />
              </button>
            )}
            {wa && (
              <a
                href={`https://wa.me/+${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-btn"
                title="Chat on WhatsApp"
                aria-label={`Chat on WhatsApp with ${item.name || 'registrant'}`}
              >
                <FaWhatsapp size={13} />
              </a>
            )}
            {isSystemAdmin && !verified && String(item.payment_method || '').toLowerCase() === 'bank transfer' && (
              <button className="btn btn-primary btn-sm" onClick={onVerify} disabled={isVerifying}>
                {isVerifying && <span className="spinner" style={{ marginRight: 6 }} />}
                {isVerifying ? 'Confirming…' : 'Confirm bank payment'}
              </button>
            )}
            {isSystemAdmin && verified && !item.EmailSentAt && (
              <button className="btn btn-primary btn-sm" onClick={onNotify} disabled={isNotifying}>
                {isNotifying && <span className="spinner" style={{ marginRight: 6 }} />}
                {isNotifying ? 'Sending…' : 'Send QR Email'}
              </button>
            )}
            {item.EmailSentAt && (
              <span className="pill pill-success" title={fmtDateTime(item.EmailSentAt)}>
                <span className="dot" />Sent {fmtBoardDate(item.EmailSentAt)}
              </span>
            )}
            <button
              className="icon-btn"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-label={open ? 'Hide details' : 'Show details'}
              title={open ? 'Hide details' : 'Show details'}
            >
              <FaAngleDown style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }} size={14} />
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-2) 0',
              }}
            >
              <Detail label="Participation" value={item.participation ?? '-'} />
              <Detail label="Amount" value={item.amount ?? '-'} />
              <Detail label="Payment Method" value={item.payment_method ?? '-'} />
              <Detail label="Contact" value={item.contact ?? '-'} />
              <Detail label="Email" value={item.email ?? '-'} />
              <Detail label="Status" value={item.status ?? '-'} />
              <Detail label="Confirmation Email" value={item.EmailSentAt ? `Sent ${fmtDateTime(item.EmailSentAt)}` : 'Not sent'} />
              {isKoko && (
                <>
                  <Detail label="KOKO Status" value={item.KokoPaymentStatus || '-'} />
                  <Detail label="KOKO Merchant Order ID" value={item.KokoMerchantOrderId || '-'} />
                  <Detail label="KOKO Transaction ID" value={item.KokoTransactionId || '-'} />
                  <Detail label="KOKO Reference" value={item.KokoReference || '-'} />
                  <Detail label="KOKO Provider Status" value={item.KokoProviderStatus || '-'} />
                  <Detail label="Signature Verified" value={item.KokoSignatureVerified === 1 || item.KokoSignatureVerified === '1' ? 'Yes' : 'No'} />
                  <Detail label="KOKO Paid At" value={fmtDateTime(item.KokoPaidAt) || '-'} />
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});
